/**
 * `/agents/goals/$id/define` — the goal-definition editor (RO-3-13 + W4-A1 UX).
 *
 * Rewrite of `excellent/app/agents/goals/[id]/define/page.tsx` (189 LOC of
 * server JSX), deferred here by RO-3-02.
 *
 * ## What made this a REWRITE, and why it is the most interesting of the five
 *
 * The original is a `<form action={defineGoal}>` — a NATIVE Next Server Action
 * form with no client JavaScript at all. The server component read the goal
 * inline (`getGoal`) to populate nine `defaultValue`s, and the browser POSTed the
 * form directly to the action, which wrote and then `redirect()`ed.
 *
 * There is no mechanical translation of that. A client route cannot post to a
 * Server Action, so this page needed BOTH halves built:
 *
 *  - the READ: `agentGoals.definition` (new in RO-3-13) — the form cannot render
 *    a single input without the goal's current definition.
 *  - the WRITE: `agentGoals.define` — the contract existed already (RO-2-09
 *    ported the action and wrote it, explicitly noting "this endpoint is the
 *    correct wiring for when that page is ported"), but it was mounted on NO
 *    namespace. RO-3-13 mounts it.
 *
 * ## Decision — ONE implementation of the write, not two
 *
 * The write itself was extracted to `excellent/src/agents/goal-define.ts`
 * (`defineGoalImpl`) and BOTH surfaces now call it: the surviving Next action and
 * the new endpoint. The textarea parsers moved with it, verbatim. This matters
 * more than it looks: the `[x]`/`[>]` mark grammar, the ` | hint` delimiter
 * (space-pipe-space, not a bare `|`) and the "blank input clears the column" rule
 * are all load-bearing on JSON already in the vault. Re-deriving them in the
 * route would have been the quiet way to fork goal-definition semantics between
 * the two stacks while both are live.
 *
 * ## W4-A1 UX (#44604) — drafts, not a blank textarea
 *
 * RECURSIVE-DONE §16 calls a blank-form wizard a regression. On open, when the
 * goal has no authored criteria yet, `agentGoals.definition` returns `drafts`
 * derived from outcome / metric / linked tasks / those tasks' DoD + gates. Each
 * draft shows its provenance `why`. The human confirms, corrects labels, or
 * removes rows — then Save writes through `defineGoalImpl` (authored seam from
 * #44603). Live Next pages still post to the server action and are not repointed.
 */

import * as React from "react";
import { createRoute } from "@tanstack/react-router";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormField,
  Input,
  Label,
  PageHeader,
  Textarea,
  parseCriteria,
  parseMilestones,
} from "@excellent/ui";
import { useApi, apiCall } from "@excellent/ui/api";
import {
  agentGoals,
  type AgentGoalDraftCriterion,
} from "@excellent/ui/api/contracts/agent-goals";
import { rootRoute } from "./__root";

/** One editable criterion row in the confirm list. */
interface CriterionRow {
  /** Stable key for React; not sent to the server. */
  key: string;
  label: string;
  done: boolean;
  /** Provenance for drafts; null for saved / hand-added rows. */
  why: string | null;
  origin: AgentGoalDraftCriterion["origin"] | "saved" | "manual";
}

/** Index-stable serialize — label edits must not re-pair done flags. */
function serializeCriteria(rows: CriterionRow[]): string {
  return rows
    .map((c) => {
      const label = c.label.trim();
      if (!label) return null;
      return `[${c.done ? "x" : " "}] ${label}`;
    })
    .filter((x): x is string => x != null)
    .join("\n");
}

/** Serialize stored milestones back to the textarea convention
 *  (`[x]` done · `[>]` current · `[ ]` upcoming, optional ` | hint`). */
function milestonesToText(json: string | null): string {
  return parseMilestones(json)
    .map((m) => {
      const mk = m.status === "done" ? "x" : m.status === "current" ? ">" : " ";
      return `[${mk}] ${m.label}${m.hint ? ` | ${m.hint}` : ""}`;
    })
    .join("\n");
}

let rowKeySeq = 0;
function nextKey(prefix: string): string {
  rowKeySeq += 1;
  return `${prefix}-${rowKeySeq}`;
}

function rowsFromStored(json: string | null): CriterionRow[] {
  return parseCriteria(json).map((c) => ({
    key: nextKey("saved"),
    label: c.label,
    done: c.done,
    why: null,
    origin: "saved" as const,
  }));
}

function rowsFromDrafts(drafts: AgentGoalDraftCriterion[]): CriterionRow[] {
  return drafts.map((d) => ({
    key: nextKey("draft"),
    label: d.label,
    done: false,
    why: d.why,
    origin: d.origin,
  }));
}

/** The eight non-criteria fields + the structured criteria list. */
interface DefineForm {
  outcome: string;
  why: string;
  metric_label: string;
  metric_baseline: string;
  metric_current: string;
  metric_target: string;
  metric_pct: string;
  criteriaRows: CriterionRow[];
  /** True when the list was seeded from server drafts (light-confirm mode). */
  fromDrafts: boolean;
  milestones: string;
}

function GoalDefinePage() {
  const { id } = agentsGoalDefineRoute.useParams();
  const goalId = Number(id);
  const navigate = agentsGoalDefineRoute.useNavigate();

  const { data, error, isLoading } = useApi(agentGoals.definition, {
    params: { id: goalId },
  });

  const [form, setForm] = React.useState<DefineForm | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const detail = data?.detail ?? null;

  // Seed the controlled fields once the definition arrives. Keyed on the goal's
  // own id so navigating between two goals' define pages re-seeds rather than
  // carrying the previous goal's text across.
  React.useEffect(() => {
    if (!detail) return;
    const stored = rowsFromStored(detail.criteria);
    const drafts = detail.drafts ?? [];
    const useDrafts = stored.length === 0 && drafts.length > 0;
    setForm({
      outcome: detail.outcome ?? "",
      why: detail.why ?? "",
      metric_label: detail.metric_label ?? "",
      metric_baseline: detail.metric_baseline ?? "",
      metric_current: detail.metric_current ?? "",
      metric_target: detail.metric_target ?? "",
      metric_pct: detail.metric_pct == null ? "" : String(detail.metric_pct),
      criteriaRows: useDrafts ? rowsFromDrafts(drafts) : stored,
      fromDrafts: useDrafts,
      milestones: milestonesToText(detail.milestones),
    });
  }, [detail]);

  const set = <K extends keyof DefineForm>(k: K) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setForm((f) => (f ? { ...f, [k]: e.target.value } : f));

  function updateRow(key: string, patch: Partial<CriterionRow>) {
    setForm((f) =>
      f
        ? {
            ...f,
            criteriaRows: f.criteriaRows.map((r) =>
              r.key === key ? { ...r, ...patch } : r,
            ),
          }
        : f,
    );
  }

  function removeRow(key: string) {
    setForm((f) =>
      f ? { ...f, criteriaRows: f.criteriaRows.filter((r) => r.key !== key) } : f,
    );
  }

  function addRow() {
    setForm((f) =>
      f
        ? {
            ...f,
            criteriaRows: [
              ...f.criteriaRows,
              {
                key: nextKey("manual"),
                label: "",
                done: false,
                why: null,
                origin: "manual",
              },
            ],
          }
        : f,
    );
  }

  async function save() {
    if (!form) return;
    setSaving(true);
    setSaveError(null);
    try {
      await apiCall(agentGoals.define, {
        params: { id: goalId },
        body: {
          outcome: form.outcome,
          why: form.why,
          metric_label: form.metric_label,
          metric_baseline: form.metric_baseline,
          metric_current: form.metric_current,
          metric_target: form.metric_target,
          metric_pct: form.metric_pct,
          criteria: serializeCriteria(form.criteriaRows),
          milestones: form.milestones,
        },
      });
      // The original action redirected to the goal on success. Same destination.
      void navigate({ to: "/agents/goals/$id", params: { id } });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  if (isLoading && !detail) {
    return (
      <div className="px-6 py-8">
        <p className="type-body text-muted-foreground">Loading goal…</p>
      </div>
    );
  }

  // The original called `notFound()` for a non-numeric id and for a missing row.
  if (error || !detail || !Number.isFinite(goalId) || goalId <= 0) {
    return (
      <div className="@container/page flex flex-col gap-4 px-6 py-8">
        <PageHeader title="Goal not found" description="No goal matches that id." />
        <Button asChild variant="outline">
          <Link href="/agents/goals">← Back to goals</Link>
        </Button>
      </div>
    );
  }

  const criteriaHelp = form?.fromDrafts
    ? "Drafted from this goal's outcome, metric, linked tasks, and their gates. Confirm, correct a label, or remove a row — then save."
    : "One per line equivalent: edit labels, mark met, add or remove. Save writes the checklist.";

  return (
    <div className="flex flex-col">
      <div className="@container/page flex flex-col gap-5 px-6 py-8">
        <PageHeader
          title="Define this goal"
          description={detail.title}
          toolbar={
            <Button asChild variant="outline">
              <Link href={`/agents/goals/${id}`}>← Back</Link>
            </Button>
          }
        />

        <Card className="max-w-2xl">
          <CardContent className="py-5">
            <form
              className="space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
            >
              <FormField
                label="Outcome"
                htmlFor="g-outcome"
                help="One sentence — what's true when this is done."
              >
                <Textarea
                  id="g-outcome"
                  name="outcome"
                  rows={2}
                  value={form?.outcome ?? ""}
                  onChange={set("outcome")}
                  placeholder="New workspaces reach first real value in under 5 minutes."
                />
              </FormField>

              <FormField label="Why it matters" htmlFor="g-why">
                <Textarea
                  id="g-why"
                  name="why"
                  rows={2}
                  value={form?.why ?? ""}
                  onChange={set("why")}
                  placeholder="The stakes — what this unlocks, or the problem it solves."
                />
              </FormField>

              <div className="rounded-md border border-border/60 p-3">
                <p className="mb-1 type-body font-medium text-foreground">
                  Definition of done — target metric
                </p>
                <p className="mb-3 type-subtext text-muted-foreground">
                  A number to move. Optional — or use the checklist below, or both.
                </p>
                <div className="space-y-3">
                  <FormField label="Metric" htmlFor="g-mlabel">
                    <Input
                      id="g-mlabel"
                      name="metric_label"
                      value={form?.metric_label ?? ""}
                      onChange={set("metric_label")}
                      placeholder="Median time to first value"
                    />
                  </FormField>
                  <div className="grid grid-cols-3 gap-3">
                    <FormField label="Baseline">
                      <Input
                        name="metric_baseline"
                        value={form?.metric_baseline ?? ""}
                        onChange={set("metric_baseline")}
                        placeholder="12 min"
                      />
                    </FormField>
                    <FormField label="Now">
                      <Input
                        name="metric_current"
                        value={form?.metric_current ?? ""}
                        onChange={set("metric_current")}
                        placeholder="7 min"
                      />
                    </FormField>
                    <FormField label="Target">
                      <Input
                        name="metric_target"
                        value={form?.metric_target ?? ""}
                        onChange={set("metric_target")}
                        placeholder="< 5 min"
                      />
                    </FormField>
                  </div>
                  <FormField label="% to target" htmlFor="g-pct" help="0–100">
                    <Input
                      id="g-pct"
                      name="metric_pct"
                      type="number"
                      min={0}
                      max={100}
                      value={form?.metric_pct ?? ""}
                      onChange={set("metric_pct")}
                      placeholder="71"
                    />
                  </FormField>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="type-body font-medium text-foreground">
                    Definition of done — acceptance criteria
                  </p>
                  <p className="mt-0.5 type-subtext text-muted-foreground">{criteriaHelp}</p>
                </div>

                {form?.fromDrafts ? (
                  <Alert className="px-3 py-2">
                    <AlertDescription className="type-subtext text-muted-foreground">
                      Drafted for you — light confirm. Correct any label, remove what
                      does not apply, then save to bind as authored criteria.
                    </AlertDescription>
                  </Alert>
                ) : null}

                <ul className="flex flex-col gap-2" data-testid="criteria-draft-list">
                  {(form?.criteriaRows ?? []).map((row) => (
                    <li
                      key={row.key}
                      className="rounded-md border border-border/60 px-3 py-2"
                      data-origin={row.origin}
                    >
                      <div className="flex items-start gap-2">
                        <div className="mt-2 flex shrink-0 items-center gap-1.5">
                          <Checkbox
                            id={`criterion-met-${row.key}`}
                            className="size-3.5"
                            checked={row.done}
                            onCheckedChange={(v) =>
                              updateRow(row.key, { done: v === true })
                            }
                            aria-label="Met"
                          />
                          <Label
                            htmlFor={`criterion-met-${row.key}`}
                            className="type-subtext font-normal text-muted-foreground"
                          >
                            Met
                          </Label>
                        </div>
                        <div className="min-w-0 flex-1 space-y-1">
                          <Input
                            value={row.label}
                            onChange={(e) => updateRow(row.key, { label: e.target.value })}
                            placeholder="Acceptance criterion"
                            aria-label="Criterion label"
                          />
                          {row.why ? (
                            <p className="type-subtext text-muted-foreground" data-testid="draft-why">
                              {row.why}
                            </p>
                          ) : null}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRow(row.key)}
                          aria-label="Remove criterion"
                        >
                          Remove
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>

                {(form?.criteriaRows.length ?? 0) === 0 ? (
                  <p className="type-subtext text-muted-foreground">
                    No criteria yet — add one, or fill outcome / metric / link tasks
                    and reopen to draft.
                  </p>
                ) : null}

                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={addRow}>
                    Add criterion
                  </Button>
                  {form?.fromDrafts ? (
                    <span className="type-subtext text-muted-foreground">
                      Confirm = save the list as shown (after any corrections).
                    </span>
                  ) : null}
                </div>
              </div>

              <FormField
                label="Milestones"
                htmlFor="g-milestones"
                help="The named checkpoints on the way. One per line: [x] done · [>] in progress · [ ] upcoming. Add an optional marker with ` | hint`."
              >
                <Textarea
                  id="g-milestones"
                  name="milestones"
                  rows={5}
                  value={form?.milestones ?? ""}
                  onChange={set("milestones")}
                  placeholder={
                    "[x] Instrument time-to-value\n[>] Welcome wizard 5 → 3 steps | Q3\n[ ] Pre-seed demo data"
                  }
                />
              </FormField>

              {saveError ? <p className="type-subtext text-destructive">{saveError}</p> : null}

              <div className="flex justify-end gap-2">
                <Button asChild variant="outline">
                  <Link href={`/agents/goals/${id}`}>Cancel</Link>
                </Button>
                <Button type="submit" disabled={saving || !form}>
                  {saving
                    ? "Saving…"
                    : form?.fromDrafts
                      ? "Confirm definition"
                      : "Save definition"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export const agentsGoalDefineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/goals/$id/define",
  component: GoalDefinePage,
});
